import { useCallback, useContext, useRef, useState } from 'react';
import { Transition } from 'react-transition-group';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { ChatbotContext } from './chatbot-context';
import { ChatbotMessageSource } from './chatbot-message-source';
import { ChatbotCitationDocument } from './types';
import { navigateCitedDocument } from './util';

interface ChatbotMessageSourcesProps {
  documents: ChatbotCitationDocument[];
}

interface SourcesListProps {
  documents: ChatbotCitationDocument[];
  maxItemsToShowByDefault: number | null;
  isShowAll: boolean;
  onOtherClick: () => void;
  onShowLessClick: () => void;
  onClick: (document: ChatbotCitationDocument) => void;
}

function ShowOtherSourcesButton({ num, onClick }: { num: number; onClick: () => void }) {
  const t = useTranslations('features');
  return (
    <div
      onClick={onClick}
      className="border border-[#EFF2F2] inline-block px-2 py-1 rounded-sm bg-white mb-2 mr-2 cursor-pointer group hover:bg-[#F5F5F5]"
    >
      {t('ai-helper.plus_n_other', { num })}
    </div>
  );
}

function ShowLessButton({ onClick }: { onClick: () => void }) {
  const t = useTranslations('features');
  return (
    <div
      onClick={onClick}
      className="border border-[#EFF2F2] inline-block px-2 py-1 rounded-sm bg-white mb-2 mr-2 cursor-pointer group hover:bg-[#F5F5F5]"
    >
      {t('ai-helper.show_less')}
    </div>
  );
}

function SourcesList({
  documents,
  maxItemsToShowByDefault,
  isShowAll,
  onOtherClick,
  onShowLessClick,
  onClick,
}: SourcesListProps) {
  if (!maxItemsToShowByDefault) {
    return null;
  }

  let needToShowOtherButton = false;
  let needToShowLessButton = false;
  let documentsToShow = documents;
  if (documents.length > maxItemsToShowByDefault) {
    if (!isShowAll) {
      documentsToShow = documents.slice(0, maxItemsToShowByDefault);
      needToShowOtherButton = true;
    } else {
      needToShowLessButton = true;
    }
  }

  return (
    <>
      {documentsToShow.map((document) => (
        <ChatbotMessageSource onClick={onClick.bind(null, document)} key={document.internalId} document={document} />
      ))}
      {needToShowOtherButton && (
        <ShowOtherSourcesButton onClick={onOtherClick} num={documents.length - maxItemsToShowByDefault} />
      )}
      {needToShowLessButton && <ShowLessButton onClick={onShowLessClick} />}
    </>
  );
}

export function ChatbotMessageSources({ documents }: ChatbotMessageSourcesProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isShowAll, setShowAll] = useState(false);
  const [maxItemsToShowByDefault, setMaxItemsToShowByDefault] = useState<number | null>(null);

  const nodeRef = useRef(null);
  const aiContext = useContext(ChatbotContext);

  const uniqueDocuments: ChatbotCitationDocument[] = [];
  const foundDocuments = new Set();
  const foundDocumentsUrls = new Set();
  // sort documents by internalId, so the first chunks will be displayed if there are chunks of the same documents in sources
  documents.sort((a, b) => {
    if (!a.internalId) {
      return 0;
    }
    return a.internalId.localeCompare(b.internalId);
  });
  documents.forEach((document) => {
    const url = document.url ? document.url : document.portalUrl;
    if (!foundDocuments.has(document.internalId) && !foundDocumentsUrls.has(url)) {
      uniqueDocuments.push(document);
      foundDocuments.add(document.internalId);
      foundDocumentsUrls.add(url);
    }
  });
  documents = uniqueDocuments;

  const onDocClick = useCallback(
    (doc: ChatbotCitationDocument) => {
      navigateCitedDocument(doc, aiContext, aiContext.portalSettings?.id);
    },
    [aiContext]
  );

  const measureNodeRefCallback = useCallback((node: HTMLDivElement) => {
    // calc how many items might be fit in a single line
    if (node) {
      let n = 0;
      const firstOffset = (node.firstChild as HTMLElement).offsetTop;
      for (let i = 0; i < node.childNodes.length; i++) {
        const offsetTop = (node.childNodes[i] as HTMLElement).offsetTop;

        // +N other button has different offsetTop than other items (by 1px), so we check delta offset
        if (Math.abs(firstOffset - offsetTop) > 1) {
          break;
        }
        n++;
      }
      // decrease by 1 because on of the items is the "other" button
      setMaxItemsToShowByDefault(n - 1);
    }
  }, []);

  const onOtherClick = useCallback(() => {
    setShowAll(true);
  }, [setShowAll]);

  const onShowLessClick = useCallback(() => {
    setShowAll(false);
  }, [setShowAll]);

  return (
    <div className="border-t border-t-[#F4F4F5] px-4 py-3 text-neutral-800 text-sm">
      <div
        className="flex flex-row cursor-pointer select-none font-semibold"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {documents.length} source{documents.length > 1 ? 's' : ''} found
        {isExpanded ? (
          <Icon className="ml-1" icon={IconsMap.ARROW_3_DOWN} />
        ) : (
          <Icon className="ml-1 rotate-180" icon={IconsMap.ARROW_3_DOWN} />
        )}
      </div>
      <div className="opacity-0 h-[0px] overflow-hidden" ref={measureNodeRefCallback}>
        <ShowOtherSourcesButton onClick={() => null} num={documents.length} />
        {documents.map((document) => (
          <ChatbotMessageSource onClick={() => null} key={document.internalId} document={document} />
        ))}
      </div>
      <Transition nodeRef={nodeRef} in={isExpanded} timeout={150} unmountOnExit={true} mountOnEnter={true}>
        {(state) => (
          <div
            ref={nodeRef}
            className={classNames('transition-opacity mt-2', {
              'opacity-0': state === 'exited' || state === 'exiting',
              'opacity-100': state === 'entered' || state === 'entering',
            })}
          >
            <SourcesList
              documents={documents}
              isShowAll={isShowAll}
              maxItemsToShowByDefault={maxItemsToShowByDefault}
              onOtherClick={onOtherClick}
              onShowLessClick={onShowLessClick}
              onClick={onDocClick}
            />
          </div>
        )}
      </Transition>
    </div>
  );
}
